/**
 * Simple Moving Average (SMA)
 *
 * Arithmetic mean of the price source over `period` bars.
 * Also exports the shared `getSource()` and `computeSMA()` / `computeEMA()`
 * helpers that are reused by most other built-in indicators.
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const smaIndicator: IndicatorDefinition = {
  id: "sma",
  name: "Simple Moving Average",
  shortName: "SMA",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "period", label: "Period", type: "number", default: 20, min: 1, max: 500 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "Open", value: "open" },
      { label: "High", value: "high" }, { label: "Low", value: "low" },
      { label: "HL2", value: "hl2" }, { label: "HLC3", value: "hlc3" },
    ]},
    { key: "color", label: "Color", type: "color", default: "#2196F3" },
  ],
  plots: [
    { key: "sma", label: "SMA", color: "#2196F3", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, source = "close", color } = params;
    const src = getSource(candles, source);
    const data: { time: number; value: number }[] = [];

    let sum = 0;
    for (let i = 0; i < src.length; i++) {
      sum += src[i];
      if (i >= period) sum -= src[i - period];
      if (i >= period - 1) {
        data.push({ time: candles[i].time, value: sum / period });
      }
    }
    return [{ key: "sma", data }];
  },
};

// ─── Shared helper: extract price source ─────────────────────────
export function getSource(candles: CandleData[], source: string): number[] {
  return candles.map(c => {
    switch (source) {
      case "open": return c.open;
      case "high": return c.high;
      case "low": return c.low;
      case "hl2": return (c.high + c.low) / 2;
      case "hlc3": return (c.high + c.low + c.close) / 3;
      case "ohlc4": return (c.open + c.high + c.low + c.close) / 4;
      default: return c.close;
    }
  });
}

/** Reusable SMA compute (returns values aligned 1:1 with input, NaN for early bars) */
export function computeSMA(values: number[], period: number): number[] {
  const result: number[] = [];
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i];
    if (i >= period) sum -= values[i - period];
    result.push(i >= period - 1 ? sum / period : NaN);
  }
  return result;
}

/** Reusable EMA compute */
export function computeEMA(values: number[], period: number): number[] {
  const result: number[] = [];
  const k = 2 / (period + 1);
  let ema = NaN;
  for (let i = 0; i < values.length; i++) {
    if (i < period - 1) {
      result.push(NaN);
    } else if (i === period - 1) {
      // Seed with SMA
      let sum = 0;
      for (let j = 0; j < period; j++) sum += values[j];
      ema = sum / period;
      result.push(ema);
    } else {
      ema = values[i] * k + ema * (1 - k);
      result.push(ema);
    }
  }
  return result;
}
